// 2400 frames at the ~25-50 snapshot/s capture rate covers several minutes of
// play; the recording is keyed to one gameId so a single match fits in the ring.
const MAX_REPLAY_FRAMES = 6000;
const DEFAULT_REPLAY_FRAME_MS = 20;
// Capture cadence is 1-2 sim ticks (20-40ms). Keep the bounds tight so playback
// runs at true game time; the upper cap only guards against pause/hitch gaps.
const MIN_REPLAY_FRAME_MS = 8;
const MAX_REPLAY_FRAME_MS = 250;
const NORMAL_REPLAY_SPEED = 1;
const FAST_REPLAY_SPEED = 4;

const replay = {
  recording: false,
  playback: false,
  paused: true,
  frames: [],
  cursor: -1,
  renderedCursor: -1,
  lastAdvanceAt: 0,
  direction: 1,
  speed: NORMAL_REPLAY_SPEED,
  // Sub-frame progress [0,1) from `cursor` toward the next frame, used to
  // interpolate head positions for smooth, real-game-matched motion.
  frac: 0,
  gameId: ''
};

function cloneValue(value) {
  if (value === undefined || value === null) return value;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch (_) {
      // Fall through to JSON clone for plain snapshot data.
    }
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
}

function frameGameId(state) {
  const gameId = state?.status?.gameId;
  return typeof gameId === 'string' ? gameId : '';
}

function isGameplayState(state) {
  return Boolean(
    state
    && typeof state === 'object'
    && Array.isArray(state.players)
    && state.players.length > 0
    && state.status
    && typeof state.status === 'object'
  );
}

function resetReplayFrames() {
  replay.frames = [];
  replay.cursor = -1;
  replay.renderedCursor = -1;
  replay.lastAdvanceAt = 0;
  replay.direction = 1;
  replay.speed = NORMAL_REPLAY_SPEED;
  replay.frac = 0;
}

function replayNowMs(nowMs) {
  const parsed = Number(nowMs);
  return Number.isFinite(parsed) ? parsed : performance.now();
}

function normalizeReplayDirection(direction) {
  return Number(direction) < 0 ? -1 : 1;
}

function normalizeReplaySpeed(speed) {
  const parsed = Number(speed);
  if (!Number.isFinite(parsed)) return NORMAL_REPLAY_SPEED;
  return Math.max(0.25, Math.min(8, parsed));
}

function startReplayRecording() {
  replay.recording = true;
  replay.playback = false;
  replay.paused = true;
  replay.gameId = '';
  resetReplayFrames();
}

function stopReplayRecording() {
  replay.recording = false;
}

function clearReplay() {
  replay.recording = false;
  replay.playback = false;
  replay.paused = true;
  replay.gameId = '';
  resetReplayFrames();
}

function recordReplayFrame(state, ops = [], meta = {}) {
  if (!replay.recording || !isGameplayState(state)) return false;

  // Whenever the captured game changes (e.g. a lingering previous-match frame
  // followed by a fresh `/live` start), drop the old frames and adopt the new
  // gameId. Failing to update gameId here would re-trigger the reset on every
  // frame and the buffer would never accumulate.
  const nextGameId = frameGameId(state);
  if (nextGameId && nextGameId !== replay.gameId) {
    if (replay.gameId) resetReplayFrames();
    replay.gameId = nextGameId;
  }

  const frame = {
    state: cloneValue(state),
    ops: Array.isArray(ops) ? cloneValue(ops) : [],
    tick: Number.isFinite(Number(meta.simTick)) ? Number(meta.simTick) : (Number(state.tick) || 0),
    serverNow: Number.isFinite(Number(meta.serverNow)) ? Number(meta.serverNow) : (Number(state.serverNow) || Date.now()),
    atMs: Number.isFinite(Number(meta.atMs)) ? Number(meta.atMs) : Date.now()
  };
  replay.frames.push(frame);
  if (replay.frames.length > MAX_REPLAY_FRAMES) {
    const removed = replay.frames.length - MAX_REPLAY_FRAMES;
    replay.frames.splice(0, removed);
    if (replay.cursor >= 0) {
      replay.cursor = Math.max(0, replay.cursor - removed);
    }
    if (replay.renderedCursor >= 0) {
      replay.renderedCursor = Math.max(-1, replay.renderedCursor - removed);
    }
  }
  if (!replay.playback) {
    replay.cursor = replay.frames.length - 1;
  }
  return true;
}

function startReplayPlayback(nowMs = performance.now()) {
  if (replay.frames.length === 0) return false;
  // Freeze the capture: playback and recording are mutually exclusive, so live
  // snapshots can no longer mutate the buffer underneath the cursor.
  replay.recording = false;
  replay.playback = true;
  replay.paused = false;
  replay.direction = 1;
  replay.speed = NORMAL_REPLAY_SPEED;
  replay.frac = 0;
  if (replay.cursor < 0 || replay.cursor >= replay.frames.length - 1) {
    replay.cursor = 0;
  }
  replay.renderedCursor = -1;
  replay.lastAdvanceAt = replayNowMs(nowMs);
  return true;
}

function stopReplayPlayback() {
  replay.playback = false;
  replay.paused = true;
  replay.renderedCursor = -1;
  replay.direction = 1;
  replay.speed = NORMAL_REPLAY_SPEED;
  replay.frac = 0;
}

function setReplayPaused(paused, nowMs = performance.now()) {
  replay.paused = Boolean(paused);
  replay.lastAdvanceAt = replayNowMs(nowMs);
  if (replay.paused) replay.frac = 0;
}

function toggleReplayPaused(nowMs = performance.now()) {
  setReplayPaused(!replay.paused, nowMs);
  return replay.paused;
}

function seekReplayFrame(index, pause = true, nowMs = performance.now()) {
  if (replay.frames.length === 0) return false;
  replay.playback = true;
  replay.cursor = Math.max(0, Math.min(replay.frames.length - 1, Math.floor(Number(index) || 0)));
  replay.paused = Boolean(pause);
  replay.direction = 1;
  replay.speed = NORMAL_REPLAY_SPEED;
  replay.frac = 0;
  replay.lastAdvanceAt = replayNowMs(nowMs);
  return true;
}

function stepReplayFrame(delta, nowMs = performance.now()) {
  if (replay.frames.length === 0) return false;
  const current = replay.cursor >= 0 ? replay.cursor : 0;
  return seekReplayFrame(current + (Number(delta) || 0), true, nowMs);
}

function latestReplayFrame(nowMs = performance.now()) {
  return seekReplayFrame(replay.frames.length - 1, true, nowMs);
}

function startReplayScan(direction = 1, speed = FAST_REPLAY_SPEED, nowMs = performance.now()) {
  if (replay.frames.length === 0) return false;
  const dir = normalizeReplayDirection(direction);
  replay.recording = false;
  replay.playback = true;
  replay.paused = false;
  replay.direction = dir;
  replay.speed = normalizeReplaySpeed(speed);
  replay.frac = 0;
  if (replay.cursor < 0) {
    replay.cursor = dir < 0 ? replay.frames.length - 1 : 0;
  } else if (dir > 0 && replay.cursor >= replay.frames.length - 1) {
    replay.cursor = 0;
  } else if (dir < 0 && replay.cursor <= 0) {
    replay.cursor = replay.frames.length - 1;
  }
  replay.lastAdvanceAt = replayNowMs(nowMs);
  return true;
}

// Real game-time gap between two captured frames. Prefer the server simulation
// clock (serverNow) so playback runs at true game speed regardless of the
// client-side jitter present in wall-clock receive times; fall back to atMs.
function frameDurationMs(index) {
  const current = replay.frames[index];
  const next = replay.frames[index + 1];
  if (!current || !next) return DEFAULT_REPLAY_FRAME_MS;
  let delta = Number(next.serverNow) - Number(current.serverNow);
  if (!Number.isFinite(delta) || delta <= 0) {
    delta = Number(next.atMs) - Number(current.atMs);
  }
  if (!Number.isFinite(delta) || delta <= 0) return DEFAULT_REPLAY_FRAME_MS;
  return Math.max(MIN_REPLAY_FRAME_MS, Math.min(MAX_REPLAY_FRAME_MS, delta));
}

function advanceReplay(nowMs = performance.now()) {
  if (!replay.playback || replay.paused || replay.frames.length === 0) {
    replay.frac = 0;
    return false;
  }
  const now = replayNowMs(nowMs);
  if (replay.cursor < 0) {
    replay.cursor = replay.direction < 0 ? replay.frames.length - 1 : 0;
    replay.lastAdvanceAt = now;
    replay.frac = 0;
    return true;
  }
  const direction = normalizeReplayDirection(replay.direction);
  if ((direction > 0 && replay.cursor >= replay.frames.length - 1) || (direction < 0 && replay.cursor <= 0)) {
    replay.paused = true;
    replay.lastAdvanceAt = now;
    replay.frac = 0;
    return false;
  }

  const lastAdvanceAt = Number(replay.lastAdvanceAt);
  let elapsed = Math.max(0, now - (Number.isFinite(lastAdvanceAt) ? lastAdvanceAt : now));
  let changed = false;
  const speed = normalizeReplaySpeed(replay.speed);
  while (
    (direction > 0 && replay.cursor < replay.frames.length - 1)
    || (direction < 0 && replay.cursor > 0)
  ) {
    const frameDurationIndex = direction > 0 ? replay.cursor : replay.cursor - 1;
    const duration = frameDurationMs(frameDurationIndex) / speed;
    if (elapsed < duration) break;
    elapsed -= duration;
    replay.cursor += direction;
    changed = true;
  }
  replay.lastAdvanceAt = now - elapsed;
  const atEnd = (direction > 0 && replay.cursor >= replay.frames.length - 1)
    || (direction < 0 && replay.cursor <= 0);
  if (atEnd) {
    replay.paused = true;
    replay.frac = 0;
  } else {
    // Fractional progress into the current frame's interval, for head interpolation.
    const frameDurationIndex = direction > 0 ? replay.cursor : replay.cursor - 1;
    const duration = frameDurationMs(frameDurationIndex) / speed;
    replay.frac = duration > 0 ? Math.max(0, Math.min(1, elapsed / duration)) : 0;
  }
  return changed;
}

function currentReplayFrame() {
  if (!replay.playback || replay.cursor < 0) return null;
  return replay.frames[replay.cursor] || null;
}

function replayHasContent() {
  return replay.frames.length > 0;
}

// Serialise the captured session into a compact JSON log (one entry per
// captured frame) for offline evaluation. Returns null when nothing is
// recorded.
function replayPlaybackProgress() {
  if (!replay.playback || replay.cursor < 0 || replay.frames.length === 0) {
    return { frame: null, next: null, frac: 0 };
  }
  const frame = replay.frames[replay.cursor] || null;
  const dir = normalizeReplayDirection(replay.direction);
  const nextIndex = replay.cursor + dir;
  const hasNext = replay.frac > 0 && nextIndex >= 0 && nextIndex < replay.frames.length;
  return {
    frame,
    next: hasNext ? replay.frames[nextIndex] : null,
    frac: hasNext ? replay.frac : 0
  };
}

function markReplayRendered() {
  replay.renderedCursor = replay.cursor;
}

// Concatenate the trail ops for frames [from, to] (inclusive). Trail ops are
// incremental paint deltas, so re-applying a contiguous range reconstructs the
// trails for that span exactly.
function replayOpsInRange(from, to) {
  if (replay.frames.length === 0) return [];
  const start = Math.max(0, Math.floor(Number(from) || 0));
  const end = Math.min(replay.frames.length - 1, Math.floor(Number(to)));
  const ops = [];
  for (let i = start; i <= end; i += 1) {
    const frameOps = replay.frames[i]?.ops;
    if (Array.isArray(frameOps) && frameOps.length > 0) {
      ops.push(...frameOps);
    }
  }
  return ops;
}

// Decide how to repaint the field for the current cursor:
//  - `null`   → nothing changed since the last render, skip the repaint
//  - `append` → cursor moved forward; only paint the newly-passed frames
//  - `full`   → first render, backward seek, or jump; clear and rebuild 0..cursor
function replayRenderPlan() {
  if (!replay.playback || replay.cursor < 0 || replay.frames.length === 0) return null;
  if (replay.cursor === replay.renderedCursor) return null;
  if (replay.renderedCursor >= 0 && replay.cursor > replay.renderedCursor) {
    return { mode: 'append', from: replay.renderedCursor + 1, to: replay.cursor };
  }
  return { mode: 'full', from: 0, to: replay.cursor };
}

function replayStatus() {
  return {
    recording: replay.recording,
    playback: replay.playback,
    paused: replay.paused,
    frames: replay.frames.length,
    cursor: replay.cursor,
    direction: replay.direction,
    speed: replay.speed
  };
}

export {
  startReplayRecording,
  stopReplayRecording,
  clearReplay,
  recordReplayFrame,
  startReplayPlayback,
  stopReplayPlayback,
  setReplayPaused,
  toggleReplayPaused,
  stepReplayFrame,
  latestReplayFrame,
  startReplayScan,
  advanceReplay,
  currentReplayFrame,
  replayHasContent,
  replayPlaybackProgress,
  replayRenderPlan,
  markReplayRendered,
  replayOpsInRange,
  replayStatus
};
